export const ALLOWED_IMAGE_EXT = [
  '.heic',
  '.jpg',
  '.jpeg',
  '.png',
] as const;

export const ALLOWED_ASSET_EXT = [
  ...ALLOWED_IMAGE_EXT,
  '.avi',
  '.doc',
  '.docx',
  '.m4a',
  '.mov',
  '.mp3',
  '.mp4',
  '.pdf',
  '.wav',
] as const;

export const ASSET_KINDS = ['AUDIO', 'DOCUMENT', 'IMAGE', 'VIDEO'] as const;

export type AssetKindT = typeof ASSET_KINDS[number];

type GenericAssetT = {
  id: string;
  kind: AssetKindT;
  owner: string;
  public: boolean;
  url: string;
};

export type ImagePropsT = {
  /** The main point of interest (e.g. a face), auto-detected at upload,
   *  as percentages (0-100) of the image width and height. */
  focus?: {
    x: number;
    y: number;
  };

  /** If the original image is larger than 1080x1080 square, this points
   *  to a downscaled version, fit into 1080x1080 square. */
  s1080?: {
    url: string;
  };

  /** If the original image is larger than 120x120 square, this points
   *  to a downscaled version, fit into 1080x1080 square. */
  s120?: {
    url: string;
  };

  /** If the original image is larger than 360x360 square, this points
   *  to a downscaled version, fit into 360x360 square. */
  s360?: {
    url: string;
  };
};

export type StoragePropsT = {
  contentType: null | string;

  // Exists only for public assets.
  downloadUrl: null | string;

  name: string;
  size: number;
};

export type AudioAssetT = GenericAssetT & {
  kind: 'AUDIO';
  storageProperties: StoragePropsT;
};

export type DocumentAssetT = GenericAssetT & {
  kind: 'DOCUMENT';
  storageProperties: StoragePropsT;
};

export type ImageAssetT = GenericAssetT & {
  imageProperties: ImagePropsT;
  kind: 'IMAGE';
  storageProperties: StoragePropsT;
};

// TODO: To be elaborated.
export type VideoAssetT = GenericAssetT & {
  kind: 'VIDEO';
};

export type AssetT
  = | AudioAssetT
    | DocumentAssetT
    | ImageAssetT
    | VideoAssetT;

export function deduceAssetKind(url: string): AssetKindT {
  const dotIdx = url.lastIndexOf('.');
  if (dotIdx <= 0) throw Error(`The URL has no extension (${url})`);

  const ext = url.slice(dotIdx).toLocaleLowerCase();
  switch (ext) {
    case '.avi':
    case '.mov':
    case '.mp4':
      return 'VIDEO';

    case '.doc':
    case '.docx':
    case '.pdf':
      return 'DOCUMENT';

    case '.heic':
    case '.jpeg':
    case '.jpg':
    case '.png':
      return 'IMAGE';

    case '.m4a':
    case '.mp3':
    case '.wav':
      return 'AUDIO';

    default: throw Error(`Unexpected extension "${ext}"`);
  }
}

export type AssetRefT = {
  asset: string;
  id: string;
  owner: string;
  use: string;
};
